import { useEffect, useCallback } from 'react';

// Storage constants
const APPOINTMENTS_STORAGE_KEY = 'patientAppointments';
const APPOINTMENTS_TIMESTAMP_KEY = 'patientAppointmentsTimestamp';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache duration

export const usePatientAppointmentStorage = (loggedInUser, appointments, isInitialLoad, dispatch, token) => {
    
    // Load appointments from localStorage on component mount
    const loadAppointmentsFromStorage = useCallback(() => {
        let storedData, storedTimestamp;
        try {
            storedData = localStorage.getItem(APPOINTMENTS_STORAGE_KEY);
            storedTimestamp = localStorage.getItem(APPOINTMENTS_TIMESTAMP_KEY);
            
            if (storedData && storedTimestamp) {
                const parsedAppointments = JSON.parse(storedData);
                const timestamp = parseInt(storedTimestamp);
                const now = Date.now();
                
                // Check if cache is still valid
                if (now - timestamp < CACHE_DURATION) {
                    // Dispatch cached appointments to Redux
                    dispatch({
                        type: 'setAppointmentsForUser',
                        payload: parsedAppointments
                    });
                } else {
                    // Cache expired, clear it
                    localStorage.removeItem(APPOINTMENTS_STORAGE_KEY);
                    localStorage.removeItem(APPOINTMENTS_TIMESTAMP_KEY);
                }
            }
        } catch (error) {
            console.error('Error loading appointments from storage:', error);
        }
    }, [dispatch]);

    useEffect(() => {
        if (loggedInUser && loggedInUser._id) {
            loadAppointmentsFromStorage();
        }
    }, [loggedInUser, loadAppointmentsFromStorage]); // Run when user changes


    // Save appointments to localStorage whenever they change
    useEffect(() => {
        if (appointments && appointments.length > 0 && !isInitialLoad) {
            saveAppointmentsToStorage(appointments);
        }
    }, [appointments, isInitialLoad]);

    // Clear storage when user logs out
    useEffect(() => {
        return () => {
            if (!loggedInUser) {
                clearAppointmentsFromStorage();
            }
        };
    }, [loggedInUser]);

    
    const saveAppointmentsToStorage = (appointmentsData) => {
        try {
            localStorage.setItem(APPOINTMENTS_STORAGE_KEY, JSON.stringify(appointmentsData));
            localStorage.setItem(APPOINTMENTS_TIMESTAMP_KEY, Date.now().toString());
        } catch (error) {
            console.error('Error saving appointments to storage:', error);
        }
    };

    const clearAppointmentsFromStorage = () => {
        try {
            localStorage.removeItem(APPOINTMENTS_STORAGE_KEY);
            localStorage.removeItem(APPOINTMENTS_TIMESTAMP_KEY);
        } catch (error) {
            console.error('Error clearing appointments from storage:', error);
        }
    };

    return {
        loadAppointmentsFromStorage,
        saveAppointmentsToStorage,
        clearAppointmentsFromStorage
    };
};
